import "dexie-export-import";

import Dexie, { type IndexableType, type Table } from "dexie";
import { v4 as uuidv4 } from "uuid";

export interface DebugEvent {
    timestamp: number;
    type: string;
    data: unknown;
}
interface Settings {
    id: "general";
    debug?: boolean;
    sim_score_threshold?: number;
    sim_k_limit?: number;
    stream?: boolean;
    suggestions?: boolean;
    use_agents?: boolean;
    chat_vector_index?: string;
}

export enum ChatRole {
    SYSTEM = "system",
    USER = "user",
    ASSISTANT = "assistant",
}

export const CTS_USER_CHAT = "cts_user_chat";

export enum ColumnType {
    STRING = "string",
    NUMBER = "number",
    BOOLEAN = "boolean",
    DATE = "date",
    DATE_RANGE = "date_range",
    DICT = "dict",
    ARRAY = "array",
    URL = "url",
}

export type IndexDescription = {
    description: string;
    header_name: string;
    name: string;
    type: ColumnType;
    end_field?: string;
};

export type DataSourceWithIndexDescription = {
    namespace: string;
    index_description: IndexDescription[];
};

export type IndexInfo = {
    index_prefix: string;
    name: string;
    label: string;
    product_lines?: string[];
    version?: string;
    default_date_field?: string;
    index_description: IndexDescription[];
};

export type RawDatasource = {
    label: string;
    id: string;
    enable_chat_with_document: boolean;
    index: IndexInfo[];
};

export type ChatVectorIndexOption = {
    label: string;
    id: string;
    datasource_id: string;
    datasource_label: string;
    index_name: string;
    enable_chat_with_document: boolean;
    index_description: IndexDescription[];
};

export class ChatEntity {
    id!: string;
    description!: string;
    totalTokens!: number;
    createdAt!: number;
    updatedAt?: Date;
    totalMessages?: number;
    vectorIndex!: string;
    suggestedMessages!: string[];

    static _(vectorIndex: string) {
        const record = new ChatEntity();
        record.id = uuidv4();
        record.description = `Chat-${record.id.slice(0, 8)}`;
        record.totalTokens = 0;
        record.createdAt = Date.now();
        record.vectorIndex = vectorIndex;
        record.suggestedMessages = [];

        return record;
    }

    setId(id: string) {
        this.id = id;
        return this;
    }

    setDescription(description: string) {
        this.description = (description || "New Chat").trim();
        return this;
    }

    setTotalTokens(totalTokens: number) {
        this.totalTokens = totalTokens;
        return this;
    }

    add() {
        return db.chats.add(this);
    }
}

interface SuggestionOption {
    text: string;
    score: number;
    freq: number;
}

export type SingleSuggestionOption = SuggestionOption & { oldWord: string };

interface Suggestion {
    text: string;
    oldWord?: string;
    offset: number;
    length: number;
    options: SuggestionOption[];
}

export interface KeySuggestion {
    key: string;
    suggestions: Suggestion[];
}
export interface Reference {
    reference_id: string;
    title: string;
    source: string;
    dataSourceType: string;
    page_numbers: Set<number>;
}

// This is the format of the reference that we get from the API.
export interface StreamedReference extends Omit<Reference, "dataSourceType" | "page_numbers"> {
    data_source_type: string;
    page_number: number | null;
}

export class MessageEntity {
    id!: IndexableType;
    toolCall = false;
    toolCallCount = 0;
    toolActivities: { internalName: string; done: boolean; error?: boolean }[] = [];
    chatId!: IndexableType;
    responseId!: string;
    content!: string;
    pretrained!: string;
    done?: boolean;
    debug?: DebugEvent[];
    hasError?: boolean;
    error?: string;
    suggestions?: SingleSuggestionOption[];
    role!: ChatRole;
    createdAt!: number;
    repliedId!: IndexableType;
    references: Reference[] = [];
    datasources?: string[];

    static _() {
        const record = new MessageEntity();
        record.id = uuidv4();
        record.createdAt = Date.now();
        return record;
    }

    setId(id: IndexableType) {
        this.id = id;
        return this;
    }

    setResponseId(responseId: string) {
        this.responseId = responseId;
        return this;
    }

    setChatId(id: IndexableType) {
        this.chatId = id;
        return this;
    }

    setContent(content?: string) {
        this.content = (content ?? "unknown response").trim();
        return this;
    }

    setRole(role: ChatRole) {
        this.role = role;
        return this;
    }

    setRepliedId(repliedId: IndexableType) {
        this.repliedId = repliedId;
        return this;
    }

    setDatasources(datasources: string[]) {
        this.datasources = datasources;
        return this;
    }

    setIsLoading() {
        this.done = false;
        return this;
    }

    toJSON(): Omit<
        MessageEntity,
        | "setId"
        | "setResponseId"
        | "setChatId"
        | "setContent"
        | "setRole"
        | "setRepliedId"
        | "setDatasources"
        | "setIsLoading"
        | "toJSON"
        | "add"
    > {
        return {
            id: this.id,
            toolCall: this.toolCall,
            toolCallCount: this.toolCallCount,
            toolActivities: this.toolActivities,
            chatId: this.chatId,
            responseId: this.responseId,
            content: this.content,
            pretrained: this.pretrained,
            done: this.done,
            debug: this.debug,
            hasError: this.hasError,
            error: this.error,
            suggestions: this.suggestions,
            role: this.role,
            createdAt: this.createdAt,
            repliedId: this.repliedId,
            references: this.references,
            datasources: this.datasources,
        };
    }

    add() {
        return db.messages.add(this.toJSON() as MessageEntity);
    }
}

class Database extends Dexie {
    chats!: Table<ChatEntity>;
    messages!: Table<MessageEntity>;
    settings!: Table<Settings>;

    constructor() {
        super("CTS-KS-7.2");
        this.version(8).stores({
            chats: "id, createdAt",
            messages: "id, chatId, responseId, createdAt",
            settings: "id",
        });

        this.on("populate", async () => {
            db.settings.add({
                id: "general",
            });
        });
    }
}
export const db = new Database();

export const updateSettings = <T extends keyof Omit<Settings, "id">>(
    key: T,
    value: Settings[T],
) => {
    db.settings.where({ id: "general" }).modify((settings) => {
        // biome-ignore lint/suspicious/noExplicitAny: Dynamic key assignment requires any
        (settings as any)[key] = value;
    });
};
